import { useEffect } from 'react'

/**
 * **Die gemeinsame Kopfleiste von NetCon.Cloud** (01.09.2026,
 * [E-289](../../../docs/ENTSCHEIDUNGEN.md#e-289)).
 *
 * Ein Web-Component vom Portal: `<netcon-header app="work">` plus ein Skript, das sich Farbe,
 * Zeichen, App-Liste und Benutzer selbst holt. **In Work ist nichts einzustellen** – auch die
 * Farbe nicht; sie kommt aus der App-Registrierung, damit dieselbe App überall gleich aussieht
 * und niemand Hex-Werte doppelt pflegt.
 *
 * ## Sie erscheint nur bei einer Keycloak-Sitzung
 *
 * Das ist keine Kosmetik, sondern eine inhaltliche Zusage. Die Leiste verspricht einen
 * App-Umschalter und eine **gemeinsame Abmeldung**. Für eine lokale Work-Sitzung ist dieses
 * Versprechen falsch: der Umschalter führte in Anwendungen, in denen man nicht angemeldet ist,
 * und „Abmelden" beendete eine Portal-Sitzung, die es gar nicht gibt.
 *
 * Entschieden wird das am `anmeldeweg` aus `auth.me` – siehe `auth/anmeldeweg.ts` am Server.
 *
 * ## Was die Einbindung bedeutet
 *
 * Das Skript läuft im **selben Origin** wie Work. Shadow DOM trennt die Stile, nicht das
 * JavaScript: es kann Works DOM lesen und die API im Namen des Angemeldeten aufrufen. Bei einem
 * Skript aus dem eigenen Portal ist das vertretbar – aber es ist eine **Vertrauensentscheidung**
 * und keine bloße Einbettung, und sie ist bewusst so getroffen (Nils, 01.09.2026: *„ja deswegen
 * alles im *.netcon.cloud kosmos"*).
 *
 * Praktische Folge für später: wird eine Content-Security-Policy gesetzt
 * ([nginx-kopfzeilen.md](../../../docs/nginx-kopfzeilen.md)), muss `netcon.cloud` dort in
 * `script-src`, `connect-src` und `img-src` stehen. Sonst ist die Leiste stumm weg – CSP
 * blockiert ohne sichtbare Meldung.
 */

const SKRIPT = 'https://netcon.cloud/embed/header.v1.js'

/**
 * **Das Element dem Typsystem bekannt machen.**
 *
 * `<netcon-header>` ist ein Custom Element und steht in keiner React-Typdefinition. Seit React
 * 19 liegt `JSX` im Modul `react` und nicht mehr global – eine Erweiterung von `global.JSX`
 * würde still nichts bewirken und der Typecheck bliebe rot.
 */
declare module 'react' {
  // eslint-disable-next-line @typescript-eslint/no-namespace
  namespace JSX {
    interface IntrinsicElements {
      'netcon-header': { app?: string; className?: string }
    }
  }
}

export function NetconKopfleiste() {
  useEffect(() => {
    /**
     * **Einmal laden, nie wieder.**
     *
     * Das Element registriert sich als Custom Element; ein zweites `<script>` desselben Inhalts
     * ruft `customElements.define` mit einem schon vergebenen Namen auf und wirft. Die Prüfung
     * auf das vorhandene `<script>` ist deshalb keine Sparsamkeit, sondern nötig – React hängt
     * Komponenten beim Navigieren aus und wieder ein.
     */
    if (document.querySelector(`script[src="${SKRIPT}"]`)) return
    const s = document.createElement('script')
    s.src = SKRIPT
    s.defer = true
    document.head.appendChild(s)
  }, [])

  /**
   * **`nc-work-01`** – der Schlüssel, unter dem Work im Portal registriert ist (Nils,
   * 01.09.2026). **Nicht `work`**, wie zuerst angenommen.
   *
   * Das hat eine Folge, die man leicht übersieht: die Rückfall-Konvention des Portals sucht die
   * Zeichen unter `/netcon-<schlüssel>-mark.svg`, hier also `/netcon-nc-work-01-mark.svg` – und
   * die gibt es nicht, Works Dateien heißen `netcon-work-…`. **Der Steckbrief unter
   * `/.well-known/netcon-app.json` ist für Work deshalb keine Bequemlichkeit, sondern nötig**:
   * er nennt die Pfade ausdrücklich.
   *
   * Der Bereich `slot="nav"` gehört Work – hier bewusst leer: Works eigene Navigation steht in
   * der Seitenleiste, und sie oben zu wiederholen wäre eine zweite Stelle, an der ein Punkt
   * fehlen kann.
   *
   * **Sie klebt oben** (17.09.2026). Seitenleiste, Reiterleisten und Tabellenköpfe kleben mit
   * `unter-leiste` 48 px unter ihr ([E-325](../../../docs/ENTSCHEIDUNGEN.md#e-325)). Solange die
   * Leiste selbst mitscrollte, blieb beim Scrollen oben ein 48 px hoher Streifen, durch den der
   * Inhalt lief – sichtbar etwa unter „KI & Einstellungen → Stand". `:host{all:initial}` im
   * Skript steht dem nicht entgegen: Stile von außen schlagen `:host`-Regeln. `z-[90]` liegt
   * über Seitenleiste (50) und klebenden Leisten, unter dem „Neue Fassung"-Band (100), das
   * ohnehin unter ihr beginnt.
   */
  return <netcon-header app="nc-work-01" className="sticky top-0 z-[90]" />
}
